import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm"
          : "bg-background-50/80 backdrop-blur-md border-b border-background-200/40"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-950 hover:text-foreground-700 transition-colors"
          >
            <span className="font-heading text-xl font-bold tracking-tight whitespace-nowrap">
              Kakobuy <span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Home
            </Link>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-background-50 rounded-xl border border-background-200/70 py-3 shadow-lg max-h-96 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-base text-foreground-500`}></i>
                      </span>
                      <span className="whitespace-nowrap">{cat.name}</span>
                      <i className="ri-external-link-line text-xs text-foreground-400 ml-auto"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/guide/how-to-use-kakobuy-spreadsheet"
              className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Shopping Guide
            </Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap rounded-md hover:bg-background-100"
            >
              Kakobuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center text-foreground-900 cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70 px-4 pb-4 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col pt-2">
            {/* Primary Nav Items */}
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-home-4-line text-base text-foreground-600"></i>
              </span>
              Home
            </Link>

            <Link
              to="/guide/how-to-use-kakobuy-spreadsheet"
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-book-open-line text-base text-foreground-600"></i>
              </span>
              Shopping Guide
            </Link>

            {/* Collapsible Categories */}
            <div className="mt-1">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-grid-line text-base text-foreground-600"></i>
                  </span>
                  Categories
                </span>
                <i
                  className={`text-base text-foreground-500 transition-transform duration-200 ${
                    mobileCatOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
                  }`}
                ></i>
              </button>

              {mobileCatOpen && (
                <div className="grid grid-cols-2 gap-1 px-2 pt-1 pb-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-xs text-foreground-500`}></i>
                      </span>
                      <span className="truncate text-xs">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-3 text-sm font-semibold text-center text-foreground-800 border border-background-300 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
                onClick={() => setMobileMenuOpen(false)}
              >
                Kakobuy Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-3 text-sm font-semibold text-center bg-primary-500 text-background-50 dark:text-foreground-950 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                onClick={() => setMobileMenuOpen(false)}
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}